// The screen's side of GET /api/me/events (core's events/routes.ts).
//
// Promise: `openEventStream` hands every event to `onEvent` once, in order, and
// survives disconnects without gaps. It does not rely on EventSource's own
// reconnect: under the desktop app's scheme that reconnect does not send
// Last-Event-ID. Instead, on error it closes the EventSource and opens a new one
// with `?after=<last cursor>` after `retryMs`. The server never resends what
// comes at or before the cursor it is given, so the screen does not compare
// cursors (they are opaque strings).
//
// A cursor the server refuses (400: malformed, or from another database after a
// move) cannot be resumed: EventSource does not show the status, so before
// reconnecting the stream asks once with fetch. On 400 it calls
// `onCursorRejected` and starts again from the head. A question with no answer
// within `checkMs` is given up and the stream opens anyway.
//
// Coming back (the window shown again after `awayMs` hidden, or the network
// back online) reopens the stream at once, after the last cursor: a phone that
// slept may hold a dead connection that the silence watch would only notice up
// to `silentMs` later, and a wait between retries may have grown to 30 s.

export interface StreamedEvent {
  /** The cursor of a stored event; null for a live notice (not stored). */
  cursor: string | null;
  type: string;
  payload: unknown;
  at: string;
  /** The whole message as sent (a host's stream may carry more than payload and at). */
  data: Readonly<Record<string, unknown>>;
}

export interface EventStreamOptions {
  url?: string;
  /** Resume after this cursor (e.g. kept from a previous window). */
  after?: string;
  /** The event types to listen for ("message" is always included). */
  types: string[];
  onEvent(event: StreamedEvent): void;
  /** Called when the stream is (re)connected, with the server's newest cursor. */
  onReady?(head: string): void;
  /** The server refused the cursor; the stream goes on from the head. */
  onCursorRejected?(): void;
  /** The stream broke (it opens again by itself; `onReady` says when). */
  onDown?(): void;
  retryMs?: number;
  /** No event nor ping for this long: the stream is taken as dead and reopened. */
  silentMs?: number;
  /** The question before reconnecting is given up after this long. */
  checkMs?: number;
  /** Hidden for at least this long: the stream is reopened when shown again. */
  awayMs?: number;
  fetch?: typeof fetch;
}

export interface EventStream {
  /** The cursor of the newest stored event received (or the head at start). */
  cursor(): string | null;
  close(): void;
}

export function openEventStream(options: EventStreamOptions): EventStream {
  const base = options.url ?? "/api/me/events";
  const retryMs = options.retryMs ?? 1000;
  // Doubles while the server stays away (up to 30 s), back to retryMs on ready.
  let delay = retryMs;
  let last: string | null = options.after ?? null;
  let source: EventSource | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let watch: ReturnType<typeof setTimeout> | null = null;
  const silentMs = options.silentMs ?? 60_000;
  const checkMs = options.checkMs ?? 10_000;
  let closed = false;

  const handle = (type: string) => (event: MessageEvent) => {
    const cursor = event.lastEventId || null;
    // Live notices have no id; stored events move the cursor.
    if (cursor !== null) last = cursor;
    const data = JSON.parse(event.data as string) as {
      payload: unknown;
      at: string;
    } & Record<string, unknown>;
    options.onEvent({ cursor, type, payload: data.payload, at: data.at, data });
  };

  const connect = () => {
    if (closed) return;
    const url =
      last === null ? base : `${base}?after=${encodeURIComponent(last)}`;
    const es = new EventSource(url);
    source = es;
    // A stream can die without an error (a sleeping phone, a dropped proxy):
    // the server pings every 25 s, so a silence longer than `silentMs`
    // means the stream is gone; it is closed and opened again.
    const touch = () => {
      if (watch) clearTimeout(watch);
      watch = setTimeout(() => {
        if (source !== es || closed) return;
        es.onerror?.(new Event("error"));
      }, silentMs);
    };
    touch();
    es.addEventListener("ping", touch);
    es.addEventListener("ready", touch);
    es.addEventListener("ready", (event) => {
      const { head } = JSON.parse((event as MessageEvent).data as string) as {
        head: string;
      };
      // A fresh start (no cursor): continue from what the server has now.
      if (last === null) last = head;
      delay = retryMs;
      options.onReady?.(head);
    });
    for (const type of new Set(["message", ...options.types]))
      es.addEventListener(type, ((event: MessageEvent) => {
        touch();
        handle(type)(event);
      }) as EventListener);
    es.onerror = () => {
      if (watch) clearTimeout(watch);
      watch = null;
      es.close();
      if (source !== es || closed) return;
      source = null;
      options.onDown?.();
      timer = setTimeout(() => void reconnect(), delay);
      delay = Math.min(delay * 2, 30_000);
    };
  };

  /** Before resuming, makes sure the server still takes the cursor. */
  // Only the newest attempt connects (coming back may start one while another waits).
  let attempt = 0;
  const reconnect = async () => {
    if (closed) return;
    const mine = ++attempt;
    if (last !== null) {
      const abort = new AbortController();
      // A question the network lost would hold the reconnect for good; past the
      // limit the stream opens anyway (and errors again if the server is away).
      const limit = setTimeout(() => abort.abort(), checkMs);
      const status = await (options.fetch ?? globalThis.fetch)(
        `${base}?after=${encodeURIComponent(last)}`,
        { signal: abort.signal, headers: { Accept: "text/event-stream" } },
      )
        .then((res) => res.status)
        .catch(() => 0)
        .finally(() => {
          clearTimeout(limit);
          abort.abort();
        });
      if (closed || mine !== attempt) return;
      if (status === 400) {
        last = null;
        options.onCursorRejected?.();
      }
    }
    connect();
  };

  /** Drops the current connection (or the wait before the next) and opens again now. */
  const reopen = () => {
    if (closed) return;
    if (timer) clearTimeout(timer);
    timer = null;
    if (watch) clearTimeout(watch);
    watch = null;
    source?.close();
    source = null;
    delay = retryMs;
    void reconnect();
  };
  const awayMs = options.awayMs ?? 3_000;
  let hiddenAt: number | null = null;
  const doc = typeof document === "undefined" ? null : document;
  const win = typeof window === "undefined" ? null : window;
  const onVisibility = () => {
    if (!doc) return;
    if (doc.visibilityState === "hidden") {
      hiddenAt ??= Date.now();
      return;
    }
    const away = hiddenAt !== null && Date.now() - hiddenAt >= awayMs;
    hiddenAt = null;
    if (away) reopen();
  };
  doc?.addEventListener("visibilitychange", onVisibility);
  win?.addEventListener("online", reopen);

  connect();
  return {
    cursor: () => last,
    close() {
      closed = true;
      if (timer) clearTimeout(timer);
      if (watch) clearTimeout(watch);
      doc?.removeEventListener("visibilitychange", onVisibility);
      win?.removeEventListener("online", reopen);
      source?.close();
      source = null;
    },
  };
}
